import type { GfxPrimitiveElementModel } from '../element-model.js';
import type {
  ElementUpdatedData,
  SurfaceBlockModel,
} from '../surface-model.js';
import { getObjectPropMeta, setObjectPropMeta } from './common.js';

type WatchFn<T extends GfxPrimitiveElementModel = GfxPrimitiveElementModel> = (
  oldValue: unknown,
  instance: T,
  local: boolean
) => void;

const watchSymbol = Symbol('watch');

/**
 * The watch decorator is used to watch the property change of the element.
 * You can thinks of it as a decorator version of `elementUpdated` slot of the surface model.
 */
export function watch<V, T extends GfxPrimitiveElementModel>(fn: WatchFn<T>) {
  return function watchDecorator(
    _: unknown,
    context: ClassAccessorDecoratorContext
  ) {
    const prop = context.name;
    return {
      init(this: GfxPrimitiveElementModel, v: V) {
        setObjectPropMeta(watchSymbol, Object.getPrototypeOf(this), prop, fn);
        return v;
      },
    } as ClassAccessorDecoratorResult<GfxPrimitiveElementModel, V>;
  };
}

function getWatchMeta(proto: unknown, prop: string | symbol): null | WatchFn {
  return getObjectPropMeta(proto, watchSymbol, prop);
}

// One subscription per surface dispatches to the changed element. Subscribing
// each model to every surface update makes opening n elements do n² checks.
const watchedSurfaces = new WeakMap<
  SurfaceBlockModel,
  {
    elements: Map<string, (payload: ElementUpdatedData) => void>;
    dispose: () => void;
  }
>();

export function initializeWatchers(
  prototype: unknown,
  receiver: GfxPrimitiveElementModel
) {
  const watchers = getObjectPropMeta(prototype, watchSymbol);
  const props = Object.keys(watchers);
  if (props.length === 0) return;

  const surface = receiver.surface;
  let watched = watchedSurfaces.get(surface);
  if (!watched) {
    const elements = new Map<string, (payload: ElementUpdatedData) => void>();
    const subscription = surface.elementUpdated.subscribe(payload =>
      elements.get(payload.id)?.(payload)
    );
    watched = { elements, dispose: () => subscription.unsubscribe() };
    watchedSurfaces.set(surface, watched);
  }

  const dispatch = (payload: ElementUpdatedData) => {
    for (const prop of props) {
      if (prop in payload.props) {
        getWatchMeta(prototype, prop)?.(
          payload.oldValues[prop],
          receiver,
          payload.local
        );
      }
    }
  };
  watched.elements.set(receiver.id, dispatch);
  const registration = watched;
  receiver['_disposable'].add(() => {
    // A replacement model may already have registered the same id.
    if (registration.elements.get(receiver.id) !== dispatch) return;
    registration.elements.delete(receiver.id);
    if (registration.elements.size === 0) {
      registration.dispose();
      watchedSurfaces.delete(surface);
    }
  });
}
